import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { importDataApi } from "@/network/network";
import { serverErrorMsg, showToast } from "@/other/utils/utils";
import { IMPORT_TYPES } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { MessageDialog } from "@bryntum/schedulerpro";

interface UploadTabProps {
  record: {
    importType: string;
  };
  context: any;
  canEdit: boolean;
  onBeforeImport: (files: File[]) => void;
  onAfterImport: (data: any) => void;
}

export const UploadTab = forwardRef<any, UploadTabProps>(
  ({ record, context, canEdit, onBeforeImport, onAfterImport }, ref) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const importEntity = async (file: File) => {
      const { importType } = record;
      const selectedBoardSlug = context.selectedBoard.record.slug;

      // Show user feedback dialog with 'Don't show again' option
      const showDialog = shouldShowAsyncDialog("CSV_IMPORT");
      showDialog &&
        context.editorHandler.openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "CSV_IMPORT" },
          { useExisting: true, by: "recordType" }
        );

      setIsImporting(true);
      try {
        const response = await importDataApi(file, selectedBoardSlug, importType);
        onAfterImport(response.data);
        return response;
      } catch (error: any) {
        if (error.response) showToast({ msg: serverErrorMsg(), type: "error", duration: 3000 });
        throw error;
      } finally {
        setIsImporting(false);
      }
    };

    // Expose importEntity method to parent via ref
    useImperativeHandle(ref, () => ({
      importEntity
    }));

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        setSelectedFile(null);
        return;
      }

      const file = files[0];

      if (file.size === 0) {
        MessageDialog.alert({
          title: "Empty File Not Allowed",
          message: "Empty file is not allowed. Please upload valid file."
        });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setSelectedFile(file);

      if (record.importType === IMPORT_TYPES.taskBudget) {
        onBeforeImport([file]);
      }
    };

    const handleImport = async () => {
      if (!selectedFile) return;
      await importEntity(selectedFile);
    };

    const handleButtonClick = () => {
      fileInputRef.current?.click();
    };

    return (
      <div className="flex flex-col items-center justify-center p-8 gap-6 h-full">
        <div className="text-center space-y-2 max-w-2xl">
          <p className="text-sm text-muted-foreground">
            To create a CSV template for importing your data, click "Export (CSV)" in the Export tab above.
          </p>
          <p className="text-sm text-muted-foreground">Don't change column names or column order.</p>
        </div>

        <div className="text-center max-w-2xl">
          <p className="text-sm text-muted-foreground mb-6">
            When importing new records (versus updating existing records), the ID field in the first column must be
            blank.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="hidden"
              disabled={!canEdit}
            />
            <Button
              onClick={handleButtonClick}
              variant="default"
              size="lg"
              disabled={!canEdit}
              className="min-w-[250px]"
            >
              Select a file from your device
            </Button>

            {selectedFile && (
              <div className="flex items-center gap-4 mt-4 p-4 border rounded-lg bg-muted/50 w-full max-w-md">
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-medium mb-1 block">Ready to import:</Label>
                  <p className="text-sm text-muted-foreground truncate">{selectedFile.name}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedFile && record.importType !== IMPORT_TYPES.taskBudget && (
          <div className="w-full max-w-2xl flex justify-end">
            <Button onClick={handleImport} disabled={isImporting || !canEdit} size="lg" className="min-w-[150px]">
              {isImporting ? "Importing..." : "Import Data"}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

UploadTab.displayName = "UploadTab";
